/**
 * Détection d'un appui long sur la carte.
 *
 * MapLibre n'expose que `contextmenu`, qui correspond au clic droit sur
 * ordinateur mais dont le déclenchement au doigt varie selon le navigateur et
 * se trouve souvent supprimé sur une surface manipulable. L'appui long étant
 * le geste d'ajout de marqueur, il est détecté ici à partir des événements de
 * pointeur, sans interférer avec le déplacement de la carte.
 */

import type { Map as MapLibreMap } from 'maplibre-gl'

/** Durée d'appui à partir de laquelle le geste est reconnu. */
const LONG_PRESS_MS = 500

/**
 * Déplacement toléré pendant l'appui, en pixels.
 *
 * Le doigt bouge toujours un peu ; au-delà, l'intention est un déplacement de
 * carte et non un appui.
 */
const MOVE_TOLERANCE_PX = 10

/**
 * Arbitre entre les deux détections d'un même appui long.
 *
 * Le minuteur et `contextmenu` décrivent le même geste, et un navigateur
 * tactile émet couramment les deux : mesuré à deux formulaires d'ajout de
 * marqueur, donc deux marqueurs, pour un seul appui. Leur ordre n'est pas
 * garanti — le navigateur reconnaît l'appui long autour de la même durée que
 * `LONG_PRESS_MS` — si bien qu'aucun des deux ne peut servir de signal
 * principal. Le premier arrivé l'emporte, le second est ignoré jusqu'au
 * prochain appui.
 */
export class LongPressGate {
  #claimed = false

  /** Un nouvel appui rouvre le droit de déclencher. */
  reopen(): void {
    this.#claimed = false
  }

  /** @returns vrai si c'est à ce signal-ci de déclencher le geste. */
  claim(): boolean {
    if (this.#claimed) return false
    this.#claimed = true
    return true
  }
}

export interface LongPressEvent {
  readonly lat: number
  readonly lon: number
  /** Position à l'écran, pour ancrer une bulle. */
  readonly x: number
  readonly y: number
}

/**
 * Écoute les appuis longs sur la carte.
 *
 * @returns Une fonction de désabonnement.
 */
export function onMapLongPress(
  map: MapLibreMap,
  handler: (event: LongPressEvent) => void,
): () => void {
  const container = map.getCanvasContainer()

  let timer: number | null = null
  let startX = 0
  let startY = 0
  let pointerId: number | null = null
  const gate = new LongPressGate()

  const cancel = (): void => {
    if (timer !== null) {
      window.clearTimeout(timer)
      timer = null
    }
    pointerId = null
  }

  const fire = (clientX: number, clientY: number): void => {
    // Point de passage unique des deux détections : c'est ici que le doublon
    // est écarté, et nulle part ailleurs.
    if (!gate.claim()) return

    // Le repère est pris sur le canevas et non sur son conteneur : ce dernier
    // n'a pas de dimensions propres, sa boîte est dégénérée, et s'en servir
    // donnerait des coordonnées fausses dès que la carte n'occupe pas le coin
    // de la fenêtre.
    const rect = map.getCanvas().getBoundingClientRect()
    const x = clientX - rect.left
    const y = clientY - rect.top
    const lngLat = map.unproject([x, y])

    handler({ lat: lngLat.lat, lon: lngLat.lng, x, y })
  }

  const onPointerDown = (event: PointerEvent): void => {
    // Un seul doigt : à deux, l'utilisateur zoome ou fait pivoter.
    if (pointerId !== null) {
      cancel()
      return
    }

    // Avant le tri par bouton, sans quoi deux clics droits d'affilée ne
    // donneraient qu'un seul menu : le second n'arme aucun minuteur et ne
    // rouvrirait donc jamais le droit de déclencher.
    gate.reopen()

    // Sur ordinateur, seul le clic gauche maintenu compte ; le clic droit
    // passe par `contextmenu`.
    if (event.pointerType === 'mouse' && event.button !== 0) return

    pointerId = event.pointerId
    startX = event.clientX
    startY = event.clientY

    timer = window.setTimeout(() => {
      timer = null
      pointerId = null
      fire(startX, startY)
    }, LONG_PRESS_MS)
  }

  const onPointerMove = (event: PointerEvent): void => {
    if (timer === null || event.pointerId !== pointerId) return

    const moved =
      Math.abs(event.clientX - startX) > MOVE_TOLERANCE_PX ||
      Math.abs(event.clientY - startY) > MOVE_TOLERANCE_PX

    if (moved) cancel()
  }

  const onContextMenu = (event: MouseEvent): void => {
    // Sur ordinateur, le clic droit est l'équivalent direct du geste.
    event.preventDefault()
    cancel()
    fire(event.clientX, event.clientY)
  }

  container.addEventListener('pointerdown', onPointerDown)
  container.addEventListener('pointermove', onPointerMove)
  container.addEventListener('pointerup', cancel)
  container.addEventListener('pointercancel', cancel)
  container.addEventListener('contextmenu', onContextMenu)

  // Un zoom à la molette ou un déplacement programmé invalide l'appui en cours.
  map.on('movestart', cancel)

  return () => {
    cancel()
    container.removeEventListener('pointerdown', onPointerDown)
    container.removeEventListener('pointermove', onPointerMove)
    container.removeEventListener('pointerup', cancel)
    container.removeEventListener('pointercancel', cancel)
    container.removeEventListener('contextmenu', onContextMenu)
    map.off('movestart', cancel)
  }
}
